import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faCloudDrizzle } from "@fortawesome/pro-regular-svg-icons"
import {
  faCloud,
  faCloudBolt,
  faCloudFog,
  faCloudRain,
  faCloudSnow,
  faQuestion,
  faSunBright
} from "@fortawesome/pro-solid-svg-icons"
import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { deepEquals } from "app/tools/common"
import { is12HoursTimeFormat } from "app/util/date/tools/common"
import { IForm } from "form/model"
import { formActionCreator } from "form/store/actions"
import {
  ALL_WEATHERS,
  ICampaignSchedule,
  ICampaignScheduleFormItem,
  IItemByDay,
  IWeather,
  SCHEDULE_DAY_BY_INDEX,
  WEATHERS
} from "../../schedule/model"
import { scheduleRPO } from "../../schedule/repository"

export async function synchronizeSchedule(form: IForm): Promise<void> {
  try {
    let schedule: ICampaignSchedule | null = null
    if (!form.context.relations.schedule && form.configuration.schedule.scheduleItems.length === 0) {
      // should never happened
      return
    }
    if (form.configuration.schedule.noSchedule || form.configuration.schedule.scheduleItems.length === 0) {
      if (form.context.relations.schedule === null) {
        return
      }
      await scheduleRPO.deleteSchedule(form.tempCampaignId)
    } else {
      schedule = getScheduleFromItems(form.configuration.schedule.scheduleItems)
      if (deepEquals(form.context.relations.schedule, schedule)) {
        return
      }
      if (!form.context.relations.schedule) {
        await scheduleRPO.createSchedule(form.tempCampaignId, schedule)
      } else {
        await scheduleRPO.updateSchedule(form.tempCampaignId, schedule)
      }
    }
    getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { schedule, scheduleError: false }))
  } catch (error) {
    console.error(error)
    getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { scheduleError: true }))
  }
}

export async function synchronizeWeather(form: IForm): Promise<void> {
  const { tempCampaignId: campaignId } = form
  const { weathers: existingWeathers } = form.context.relations
  const { weathers: toSetWeatherTypes } = form.configuration.schedule
  if (typeof campaignId !== "number") {
    return Promise.reject("campaignId must be a number")
  }
  if (campaignId < 0) {
    return Promise.reject("campaignId must be equals or greater then 0")
  }
  if (!Array.isArray(toSetWeatherTypes)) {
    return Promise.reject("weathers must be an array")
  }
  for (const toSetWeatherType of toSetWeatherTypes) {
    if (!ALL_WEATHERS.includes(toSetWeatherType)) {
      return Promise.reject(
        `Invalid value in toSetWeatherTypes. Provided ${toSetWeatherType}. Available: ${ALL_WEATHERS.join(",")}`
      )
    }
  }
  if (Array.isArray(existingWeathers)) {
    for (const existingWeather of existingWeathers) {
      if (typeof existingWeather !== "object") {
        return Promise.reject("An existing weather is invalid")
      }
    }
  }
  let hasError: boolean = false
  let serverSideWeathers: IWeather[] = []
  for (const toSetWeatherType of toSetWeatherTypes) {
    const existingWeather = !existingWeathers
      ? undefined
      : existingWeathers.find(w => w.weather_id === toSetWeatherType)
    if (!existingWeather) {
      try {
        serverSideWeathers.push(await scheduleRPO.createWeather(campaignId, toSetWeatherType))
      } catch (error) {
        hasError = true
        console.error("Unable to add weather", error)
      }
    } else {
      serverSideWeathers.push(existingWeather)
    }
  }
  if (existingWeathers) {
    for (const existingWeather of existingWeathers) {
      if (!toSetWeatherTypes.includes(existingWeather.weather_id)) {
        try {
          await scheduleRPO.deleteWeather(campaignId, existingWeather.id)
        } catch (error) {
          hasError = true
          console.error("Unable to delete weather", error)
          serverSideWeathers.push(existingWeather)
        }
      }
    }
  }
  if (hasError) {
    try {
      serverSideWeathers = await scheduleRPO.getWeathers(form.tempCampaignId)
    } catch (error) {
      // it doesn't smell good here ...
      console.error("Cannot fallback ...", error)
      return
    }
  }
  getStore().dispatch(
    formActionCreator.setRelations(form.context.appContext, { weathers: serverSideWeathers, weathersError: hasError })
  )
}

export const scheduleFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const schedule = await scheduleRPO.getSchedule(form.tempCampaignId)
    const scheduleItems = !schedule ? [] : getItemsFromSchedule(schedule)
    form.context.relations.schedule = schedule
    form.configuration.schedule.noSchedule = scheduleItems.length === 0
    form.configuration.schedule.scheduleItems = scheduleItems
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Schedule data-loader error ${String(error)}`)
  }
}

export const weatherFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const weathers = await scheduleRPO.getWeathers(form.tempCampaignId)
    form.context.relations.weathers = weathers
    form.configuration.schedule.weathers = weathers.map(w => w.weather_id)
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Weather form data-loader error ${String(error)}`)
  }
}

export const getItemsByDay = (items: ICampaignScheduleFormItem[]): IItemByDay[] => {
  const groupedByDayItems: IItemByDay[] = []
  for (const item of items) {
    const day = item.start.getDay()
    let group = groupedByDayItems.find(g => g.day === day)
    if (!group) {
      group = { day, items: [] }
      groupedByDayItems.push(group)
      groupedByDayItems.sort((g1, g2) => (g1.day < g2.day ? -1 : 1))
    }
    group.items.push(item)
  }
  return groupedByDayItems
}

export function getScheduleHoursText(start: Date, end: Date): string {
  if (start.getHours() === 0 && end.getMinutes() === 59) {
    return translate("label.all.day")
  }
  const formatedEnd = new Date(end)
  if (end.getMinutes() === 0) {
    formatedEnd.setHours(end.getHours() - 1)
    formatedEnd.setMinutes(59)
  }
  return `${getScheduleHourText(start)} - ${getScheduleHourText(formatedEnd)}`
}

export const getWeatherIcon = (weather: WEATHERS | number): IconProp => {
  if (typeof weather === "number") {
    const weatherId = weather
    const weatherObject = getStore().getState().schedule.weathers.findByProperty("id", weatherId)
    if (!weatherObject) {
      return faQuestion
    }
    weather = weatherObject.weather_id
  }
  switch (weather) {
    case WEATHERS.THUNDERSTORM: {
      return faCloudBolt
    }
    case WEATHERS.ATMOSPHERE: {
      return faCloudFog
    }
    case WEATHERS.CLEAR: {
      return faSunBright
    }
    case WEATHERS.CLOUDS: {
      return faCloud
    }
    case WEATHERS.DRIZZLE: {
      return faCloudDrizzle
    }
    case WEATHERS.RAIN: {
      return faCloudRain
    }
    case WEATHERS.SNOW: {
      return faCloudSnow
    }
  }
}

function getScheduleHourText(date: Date): string {
  if (is12HoursTimeFormat()) {
    return getPMFormattedTime(date)
  } else {
    return get24FormatedTime(date)
  }
}

function get24FormatedTime(date: Date): string {
  return `${getFormattedTime(date.getHours())}h${getFormattedTime(date.getMinutes())}`
}

function getFormattedTime(time: number): string {
  return time < 10 ? `0${time}` : String(time)
}

function getPMFormattedTime(date: Date): string {
  const hours24 = date.getHours()
  const amPm: "AM" | "PM" = hours24 >= 0 && hours24 < 12 ? "AM" : "PM"
  const hourAmPm = amPm === "PM" ? (hours24 === 13 ? 1 : hours24 - 12) : hours24 === 0 ? 12 : hours24
  return `${getFormattedTime(hourAmPm)}:${getFormattedTime(date.getMinutes())} ${amPm}`
}

function getScheduleFromItems(scheduleItems: ICampaignScheduleFormItem[]): ICampaignSchedule {
  const schedule: ICampaignSchedule = {
    campaign_id: -1,
    sunday: [],
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: []
  }
  for (const scheduleItem of scheduleItems) {
    const { start, end } = scheduleItem
    const scheduleTimes: number[] = schedule[SCHEDULE_DAY_BY_INDEX[start.getDay()]] as number[]
    if (!scheduleTimes) {
      continue
    }
    const startHour = start.getHours()
    const endHour = end.getMinutes() === 59 ? 23 : end.getHours() > startHour ? end.getHours() - 1 : end.getHours()
    for (let i = startHour; i <= endHour; i++) {
      scheduleTimes.push(i)
    }
  }
  return schedule
}

function getItemsFromSchedule(schedule: ICampaignSchedule): ICampaignScheduleFormItem[] {
  const items: ICampaignScheduleFormItem[] = []
  const days: string[] = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
  for (const day of days) {
    const times: number[] = (schedule as any)[day]
    if (times.length === 0) {
      continue
    }
    items.push(...getDatesFromDayAndTimes(days.indexOf(day), times))
  }
  return items
}

// first day is monday and last one sunday
function getDatesFromDayAndTimes(dayIndex: number, times: number[]): ICampaignScheduleFormItem[] {
  const items: ICampaignScheduleFormItem[] = []
  const currentDate = new Date()
  const dayDate = new Date(currentDate.setDate(currentDate.getDate() - currentDate.getDay() + 1 + dayIndex))
  dayDate.setHours(0)
  dayDate.setMinutes(0)
  dayDate.setSeconds(0)
  dayDate.setMilliseconds(0)
  let previousTime = -1
  let startTime = -1
  for (let i = 0; i < times.length; i++) {
    const currentTime = times[i]
    if (previousTime === -1) {
      previousTime = currentTime
      startTime = currentTime
      if (i === times.length - 1) {
        items.push(getItemFromTime(dayDate, currentTime, currentTime))
      }
    } else {
      if (previousTime + 1 !== currentTime) {
        items.push(getItemFromTime(dayDate, startTime, previousTime))
        if (i === times.length - 1) {
          items.push(getItemFromTime(dayDate, currentTime, currentTime))
        } else {
          previousTime = currentTime
          startTime = currentTime
        }
      } else {
        if (i === times.length - 1) {
          items.push(getItemFromTime(dayDate, startTime, currentTime))
        } else {
          previousTime = currentTime
        }
      }
    }
  }
  return items
}

function getItemFromTime(dayDate: Date, startTime: number, endTime: number): ICampaignScheduleFormItem {
  const start = new Date(dayDate)
  const end = new Date(dayDate)
  start.setHours(startTime)
  if (endTime === 23) {
    end.setHours(23)
    end.setMinutes(59)
    end.setSeconds(59)
    end.setMilliseconds(999)
  } else {
    end.setHours(endTime + 1)
  }
  return {
    start,
    end
  }
}
